import React, { useState } from 'react';
import {
  Box, Button, Paper, Typography, Alert, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Link,
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { CloudUpload, Download } from '@mui/icons-material';
import axios from 'axios';

const ClientImport = () => {
  const [file, setFile] = useState(null);
  const [clientType, setClientType] = useState('general');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const downloadTemplate = () => {
    const template = `name,email,phone,address,company,contact_person,notes,status,type
"John Smith","john@example.com","+1234567890","123 Main St","ABC Company","","New client","active","${clientType}"
"Jane Doe","jane@example.com","+0987654321","456 Oak Ave","XYZ Corp","Manager","Regular client","active","${clientType}"`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientType}_clients_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientType', clientType);

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await axios.post('/api/clients/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResults(response.data);
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Import Clients
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Download Template
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Download the CSV template and fill it with your client data
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={downloadTemplate}
          sx={{ mt: 2 }}
        >
          Download CSV Template
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 2: Select Client Type
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Client Type</InputLabel>
          <Select
            value={clientType}
            onChange={(e) => setClientType(e.target.value)}
            label="Client Type"
          >
            <MenuItem value="general">General</MenuItem>
            <MenuItem value="recording">Recording Studio</MenuItem>
            <MenuItem value="photography">Photography Studio</MenuItem>
            <MenuItem value="both">Both Studios</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 3: Upload CSV File
        </Typography>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id="csv-upload"
        />
        <label htmlFor="csv-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUpload />}
            disabled={loading}
          >
            Select CSV File
          </Button>
        </label>
        {file && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Selected: {file.name}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={!file || loading}
        fullWidth
      >
        {loading ? 'Importing...' : 'Import Clients'}
      </Button>

      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {results && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Import Results
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Total Processed</TableCell>
                <TableCell>{results.total}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Successfully Imported</TableCell>
                <TableCell>{results.success}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Skipped (Duplicates)</TableCell>
                <TableCell>{results.skipped}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Errors</TableCell>
                <TableCell>{results.errors?.length || 0}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          
          {results.errors?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="error">
                Errors:
              </Typography>
              {results.errors.map((err, index) => (
                <Typography key={index} variant="body2" color="error">
                  Row {err.row}: {err.error}
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ClientImport;
